import React, { useState, useMemo, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ListingActionTypes, SearchActionTypes } from '@state/features/common.types';
import { getZeroPadNumber } from '@utils/stringUtils';
import Button from '@ui/button/Button';
import SortBy from '@stone/components/listing/SortBy';
import IconButton from '@stone/ui/button/IconButton';
import useMenuScroll from '../menu/useMenuScroll';
import useMediaQueries from '@stone/hooks/useMediaQueries';
import useNavigationTool from '@stone/hooks/useNavigationTool';
import ProductFilterContext from '@stone/components/listing/ProductFilterContext';
import { ProductFilterRefinementTabs, ProductFilteRefinementAccordion } from '@stone/components/listing/ProductFilterRefinement';
const ModalDialog = React.lazy(() => import('@stone/components/modal/ModalDialog'));
const AnimateHeight = React.lazy(() => import('react-animate-height'));
const ProductFilters = (props) => {
    /* commons things */
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { keyboardIntent } = useNavigationTool();
    /* redux */
    const { refinements, selectedFilters, count, inSearch, filteredCategory, selectedSort, productSort /* , sizeGroups  */ } = useSelector((state) => state.search);
    /* Refs */
    const fallbackButton = useRef(null);
    const refinementButton = useRef(null);
    const sortingButton = useRef(null);
    const overlayposition = useRef(null);
    const listingPosRef = useRef(null);
    // const filterWrapper = useRef<HTMLDivElement>(null);
    const filterWrapper = useMenuScroll();
    /* states */
    const [expandedMenu, setExpandedMenu] = useState('');
    const [selectedRefinement, setSelectedRefinement] = useState('');
    const [filterRefinements, setFilterRefinements] = useState([]);
    const [firstRefinement, setFirstRefinement] = useState(null);
    useEffect(() => {
        //do not filter by category (as moncler) ad microTaxonomi (desktop) using in intro filteres
        let refi = refinements === null || refinements === void 0 ? void 0 : refinements.filter((refinement) => !refinement.isCategoryRefinement);
        setFilterRefinements(refi);
        setFirstRefinement((refi && refi[0]) || null);
        // console.log(refinements);
    }, [refinements]);
    const isMountRef = useRef(true);
    useEffect(() => {
        if (!isMountRef.current) {
            dispatch({
                type: inSearch ? SearchActionTypes.FETCH_SEARCH : ListingActionTypes.FETCH_LISTING,
                payload: {
                    resultsType: 'INIT',
                    tier: props.tier,
                },
            });
        }
        isMountRef.current = false;
    }, [selectedFilters, filteredCategory, selectedSort]);
    /* memos */
    const allFiltersCount = useMemo(() => {
        //selectedFilters isn't affidable on "back from PDP"
        let res = {
            value: refinements === null || refinements === void 0 ? void 0 : refinements.reduce((count, refinement) => {
                var _a;
                return count + ((_a = refinement.values.filter((refValue) => refValue === null || refValue === void 0 ? void 0 : refValue.selected)) === null || _a === void 0 ? void 0 : _a.length) || 0;
            }, 0),
            padded: '00',
        };
        res.padded = getZeroPadNumber(res.value, 2);
        return res;
    }, [refinements]);
    const resultsCount = useMemo(() => {
        let res = {
            value: count,
            padded: '00',
        };
        res.padded = getZeroPadNumber(res.value, 2);
        return res;
    }, [count]);
    const selectedSortOption = useMemo(() => {
        return productSort === null || productSort === void 0 ? void 0 : productSort.options.find((option) => {
            return option.sortingRuleId === selectedSort;
        });
    }, [selectedSort, productSort]);
    useLayoutEffect(() => {
        const onEscPressed = (e) => {
            fallbackButton.current = (expandedMenu && (expandedMenu == 'refine' ? refinementButton.current : sortingButton.current)) || null;
            if (!expandedMenu)
                return true;
            switch (e.key) {
                case 'Escape':
                    setExpandedMenu('');
                    backToContext();
                    break;
            }
        };
        document.addEventListener('keydown', onEscPressed);
        return () => {
            document.removeEventListener('keydown', onEscPressed);
        };
    }, [expandedMenu]);
    /* INTERNAL methods */
    const toggleMenu = (contextMenu, _selectedRefinement, _isHeaderButton) => {
        switch (contextMenu) {
            case 'refine':
                setSelectedRefinement(_selectedRefinement || '');
                setExpandedMenu(!!(expandedMenu === contextMenu && _isHeaderButton) ? '' : contextMenu);
                break;
            case 'sorting':
                setExpandedMenu(expandedMenu === contextMenu ? '' : contextMenu);
                setSelectedRefinement('');
                break;
        }
    };
    const resetFilters = (filterId) => {
        dispatch({
            type: inSearch ? SearchActionTypes.UPDATE_SEARCH_REFINEMENT : ListingActionTypes.UPDATE_LISTING_REFINEMENT,
            payload: {
                filterAction: 'RESET',
                tier: props.tier,
            },
        });
    };
    const backToContext = () => {
        var _a;
        (_a = fallbackButton.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const goToResults = useCallback(() => {
        requestAnimationFrame(() => {
            var _a;
            keyboardIntent && ((_a = document.getElementById('plp_tilelist')) === null || _a === void 0 ? void 0 : _a.focus());
            if (listingPosRef.current && document.documentElement.scrollTop > listingPosRef.current.offsetTop)
                window.scrollTo({ top: listingPosRef.current.offsetTop });
        });
    }, [keyboardIntent]);
    const handleBlur = useCallback((e) => {
        const currentTarget = e.currentTarget;
        // Give browser time to focus the next element
        requestAnimationFrame(() => {
            if (!currentTarget.contains(document.activeElement)) {
                setExpandedMenu('');
                setSelectedRefinement('');
            }
        });
    }, []);
    //mediaquery utils
    const { isMobile } = useMediaQueries();
    useEffect(() => {
        setExpandedMenu('');
        setSelectedRefinement('');
    }, [isMobile]);
    //MODAL HEADER
    const ModalHeader = useCallback((text) => {
        return (React.createElement("div", { className: "subnav-filters-modalheader" },
            React.createElement("h2", { className: "font__title" }, text),
            React.createElement(IconButton, { type: 'button', icon: 'close', "aria-label": t('button.close'), onClick: () => {
                    setExpandedMenu('');
                    backToContext();
                }, className: "hideborder" })));
    }, []);
    //FILTER CONTEXT
    const filterContext = useMemo(() => {
        return {
            expandedMenu,
            setExpandedMenu,
            filterRefinements,
            firstRefinement,
            selectedRefinement,
            setSelectedRefinement,
            selectedSortOption,
        };
    }, [expandedMenu, filterRefinements, selectedRefinement, selectedSortOption]);
    const topPosition = () => {
        var _a, _b, _c, _d, _e;
        let filterh = ((_c = (_b = (_a = filterWrapper === null || filterWrapper === void 0 ? void 0 : filterWrapper.refElement) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect()) === null || _c === void 0 ? void 0 : _c.height) || 0;
        let overlaypos = ((_e = (_d = overlayposition.current) === null || _d === void 0 ? void 0 : _d.getBoundingClientRect()) === null || _e === void 0 ? void 0 : _e.top) || 0;
        let scroll = window.scrollY;
        let approx = 7;
        // console.log(filterh, overlaypos, scroll, approx);
        return Math.floor(filterh + overlaypos + scroll + approx) || 0;
    };
    const printOverlay = useCallback(() => {
        return ((!isMobile &&
            createPortal(React.createElement("div", { className: classNames('subnav-filters-overlay', { show: !!expandedMenu }), "aria-hidden": true, role: "presentation", style: {
                    top: topPosition(),
                }, onClick: () => {
                    setExpandedMenu('');
                } }), document.body, 'subnav-filters-overlay')) || React.createElement(React.Fragment, null));
    }, [isMobile, expandedMenu]);
    return (React.createElement(ProductFilterContext.Provider, { value: filterContext },
        React.createElement("div", { ref: overlayposition, id: "for-overlayposition" }),
        printOverlay(),
        React.createElement("div", { ref: listingPosRef, id: "ref-position-el" }),
        React.createElement("div", { className: classNames('subnav-filters', { isSticky: filterWrapper.isSticky }), ref: filterWrapper.ref },
            React.createElement("div", { className: "subnav-header" },
                !isMobile && React.createElement("div", { className: classNames('subnav-refinements-backdrop', { 'is-open': expandedMenu === 'refine' }) }),
                React.createElement("ul", { className: "container--fluid" },
                    React.createElement("li", { className: "col-22 subnav-refinements" },
                        React.createElement("div", { className: "subnav-refinements-container" },
                            React.createElement("div", { className: "row" },
                                React.createElement("div", { className: "col-12 col-lg-3" },
                                    React.createElement(Button, { reference: refinementButton, cls: ['button--link'], type: "button", className: "subnav-button subnav-refinements-action refine-button", onClick: () => {
                                            if (!firstRefinement)
                                                return false;
                                            toggleMenu('refine', firstRefinement.attributeID, true);
                                            if (expandedMenu === 'refine') {
                                                //NOTE analytics (check on old)
                                            }
                                        }, "aria-controls": "filter-refinements", ariaExpanded: expandedMenu === 'refine', "aria-haspopup": filterRefinements.length > 0, disabled: filterRefinements.length < 1 },
                                        React.createElement("span", null, t('refine.title')),
                                        allFiltersCount.value > 0 && (React.createElement("span", { className: "filter-counter", "aria-label": t('refine.currentlyselectedfilters') + ' ' + allFiltersCount.value },
                                            "\u00A0",
                                            allFiltersCount.padded)))),
                                isMobile ? (React.createElement(ModalDialog, { isOpen: expandedMenu === 'refine', onRequestClose: () => {
                                        setExpandedMenu('');
                                    }, className: 'subnav-filters-modal', buttonInHeader: true, hideHeader: true, fullHeight: true, fullWidth: true, noPadding: true },
                                    ModalHeader(t('refine.title')),
                                    React.createElement(ProductFilteRefinementAccordion, { fromSearch: props.fromSearch, onReset: resetFilters, onApply: () => {
                                            setExpandedMenu('');
                                            goToResults();
                                        }, resultsCounter: resultsCount.padded }))) : (React.createElement(React.Fragment, null,
                                    React.createElement(ProductFilterRefinementTabs, { refinements: refinements, fromSearch: props.fromSearch, onSelected: (refinement) => {
                                            toggleMenu('refine', refinement.attributeID);
                                        }, useMotion: true, onReset: resetFilters, onApply: () => {
                                            setExpandedMenu('');
                                            goToResults();
                                        }, onBlur: handleBlur, resultsCounter: resultsCount.padded })))))),
                    React.createElement("li", { className: "sortby-item" },
                        React.createElement(Button, { cls: ['button--link'], type: "button", className: "subnav-button sorting-button", onClick: () => {
                                toggleMenu('sorting', null, true);
                                if (expandedMenu === 'sorting') {
                                    //NOTE analytics (check on old)
                                }
                            }, "aria-haspopup": "dialog", ariaExpanded: expandedMenu === 'sorting', 
                            //NOTE ariaControls="refineOptions" accessibility review
                            reference: sortingButton },
                            React.createElement("span", null, t('refine.sortby'))),
                        isMobile ? (React.createElement(ModalDialog, { isOpen: expandedMenu === 'sorting', className: 'subnav-filters-modal', onRequestClose: () => {
                                setExpandedMenu('');
                            }, buttonInHeader: true, hideHeader: true, fullHeight: true, fullWidth: true, noPadding: true },
                            ModalHeader(t('refine.sortby')),
                            React.createElement("div", { id: "filter-sorting", className: "subnav-content subnav-content-modal subnav-sortby" },
                                React.createElement("div", { className: "subnav-content-fluid" },
                                    React.createElement(SortBy, null))))) : (React.createElement(AnimateHeight, { height: expandedMenu === 'sorting' ? 'auto' : 0, id: "filter-sorting", className: "subnav-content subnav-content-drop subnav-sortby" },
                            React.createElement("div", { className: "subnav-content-fixed", onBlur: handleBlur },
                                React.createElement("div", { className: "sort" },
                                    React.createElement(SortBy, null)))))))))));
};
export default ProductFilters;
